import { useState, useRef, useCallback } from 'react';
import { getLeftOffset } from '../utils/getLeftOffset';

// Gap kept between the dropdown and the viewport edge (matches BlockToolbar's MoreButton).
const MARGIN = 12;

/**
 * Pure positioning math for a viewport-aware dropdown. Kept separate from the hook so it
 * can be unit-tested without a DOM render (see useDropdownPosition.test.js).
 *
 * Direction: prefer opening DOWNWARD so the first item stays visible under the trigger.
 * Only flip up when there isn't enough room below AND there is enough room above to fit
 * without scrolling. When both directions are tight, stay down and let the menu scroll.
 *
 * @param {{top:number, bottom:number, left:number}} rect - Trigger bounding rect.
 * @param {{innerWidth:number, innerHeight:number}} viewport - Viewport dimensions.
 * @param {Object} [options]
 * @param {number} [options.dropdownWidth=256] - Expected dropdown width (for left-offset math).
 * @param {number} [options.minSpace=280] - Min space (in the chosen direction) before it's "tight".
 * @param {number} [options.maxMenuHeight=Infinity] - Optional design cap on the menu height.
 * @returns {{dropUp:boolean, maxHeight:number, leftOffset:number}}
 */
export const computeDropdownPosition = (
  rect,
  { innerWidth, innerHeight },
  { dropdownWidth = 256, minSpace = 280, maxMenuHeight = Infinity } = {}
) => {
  const spaceBelow = innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const dropUp = spaceBelow < minSpace && spaceAbove >= minSpace;
  const available = (dropUp ? spaceAbove : spaceBelow) - MARGIN;
  const maxHeight = Math.max(0, Math.min(maxMenuHeight, available));
  const leftOffset = getLeftOffset(rect, dropdownWidth, 16, innerWidth);
  return { dropUp, maxHeight, leftOffset };
};

/**
 * Manages dropdown positioning state (open direction, scroll cap, horizontal offset) for a menu
 * that may need to open upward or shift left to stay within the viewport.
 *
 * @param {Object} [options]
 * @param {number} [options.dropdownWidth=256] - Expected width of the dropdown in pixels.
 * @param {number} [options.minSpaceBelow=280] - Min space (in the chosen direction) before it's "tight".
 * @param {number} [options.maxMenuHeight=Infinity] - Optional design cap on the menu height.
 * @returns {{
 *   menuRef: Object,
 *   dropUp: boolean,
 *   maxHeight: (number|undefined),
 *   leftOffset: number,
 *   recalculate: Function,
 * }}
 */
export const useDropdownPosition = ({
  dropdownWidth = 256,
  minSpaceBelow = 280,
  maxMenuHeight = Infinity,
} = {}) => {
  const menuRef = useRef(null);
  const [position, setPosition] = useState({ dropUp: false, maxHeight: undefined, leftOffset: 0 });

  const recalculate = useCallback(() => {
    const rect = menuRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition(
      computeDropdownPosition(
        rect,
        { innerWidth: window.innerWidth, innerHeight: window.innerHeight },
        { dropdownWidth, minSpace: minSpaceBelow, maxMenuHeight }
      )
    );
  }, [dropdownWidth, minSpaceBelow, maxMenuHeight]);

  return { menuRef, recalculate, ...position };
};
